// Pre-procesamiento de imágenes en el cliente (solo frontend).
// - Convierte imágenes a WebP calidad 80%.
// - Redimensiona a máx 1600px de ancho manteniendo proporción.
// - Bloquea archivos > 5MB tras la compresión.
// Los archivos que no son imágenes (ej. PDF) se devuelven intactos.

const MAX_WIDTH = 1600;
const WEBP_QUALITY = 0.8;
const MAX_BYTES = 5 * 1024 * 1024;

const readAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error("No se pudo leer el archivo"));
    reader.readAsDataURL(file);
  });

const loadImage = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("No se pudo procesar la imagen"));
    img.src = src;
  });

export async function optimizeImage(file) {
  if (!file) return file;

  // No es imagen (ej. PDF): se mantiene tal cual, solo validamos el peso.
  if (!file.type || !file.type.startsWith("image/")) {
    if (file.size > MAX_BYTES) {
      throw new Error("El archivo supera el límite de 5MB.");
    }
    return file;
  }

  const dataUrl = await readAsDataURL(file);
  const img = await loadImage(dataUrl);

  let width = img.naturalWidth || img.width;
  let height = img.naturalHeight || img.height;
  if (width > MAX_WIDTH) {
    height = Math.round(height * (MAX_WIDTH / width));
    width = MAX_WIDTH;
  }

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  // Fondo blanco: preserva legibilidad si la imagen original tenía transparencia.
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(img, 0, 0, width, height);

  const blob = await new Promise((resolve) =>
    canvas.toBlob(resolve, "image/webp", WEBP_QUALITY)
  );

  // Si el navegador no pudo generar WebP, devolvemos el original (validando peso).
  if (!blob) {
    if (file.size > MAX_BYTES) {
      throw new Error("El archivo supera el límite de 5MB.");
    }
    return file;
  }

  if (blob.size > MAX_BYTES) {
    throw new Error(
      "La imagen supera los 5MB tras la compresión. Usa una imagen más liviana."
    );
  }

  const baseName = (file.name || "imagen").replace(/\.[^.]+$/, "");
  return new File([blob], `${baseName}.webp`, { type: "image/webp" });
}
